import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent } from '@/ui-components/ui/dialog';
import { Download, Info, LayoutTemplate } from 'lucide-react';
import { Button } from '@/ui-components/ui/button';
import { Template } from '../../core/template.schemas';
import { collectDynamicFields } from '../../core/runtime/resolve';
import { Label } from '@/ui-components/ui/label';
import { TemplateRenderer } from '../../components/TemplateRenderer';
import { getMediaTypeIcon } from '../components/content-selector/utils';
import { SelectionStaticInputRenderer } from '../../create-post/components/SelectionInputComponents';
import { useResizeObserver } from '../hooks/useResizeObserver';
import { useTemplateExport } from '../../hooks/useTemplateExport';
import { cn } from '@/lib/utils/cn';
import { StandardDialogHeader } from '@/util-components/dialog/components/StandardDialogHeader';
import { Permission } from '@/lib/api/interfaces/permissions.interface';
import { PlanKey } from '@/lib/api/interfaces/billing.interface';
import { PermissionFeatureWrapper } from '@/util-components/auth/PermissionFeatureWrapper';
import { SentrySectionBoundary } from '@/util-components/error/sentry-section-boundary';
import { useOnboardingTourStep } from '@/util-components/onboarding/tour-step-store';

interface ImportStoreTemplateDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  template: Template | null;
  onConfirm: (overrides: Record<string, any>, thumbnailData?: string) => Promise<void>;
  isSubmitting?: boolean;
  preventOutsideClose?: boolean;
}

export const ImportStoreTemplateDialog: React.FC<ImportStoreTemplateDialogProps> = ({
  open,
  onOpenChange,
  template,
  onConfirm,
  isSubmitting = false,
  preventOutsideClose = false,
}) => {
  const [overrideValues, setOverrideValues] = useState<Record<string, any>>({});
  const { generateThumbnail } = useTemplateExport();
  const [isPreparing, setIsPreparing] = useState(false);
  const onboardingTourStep = useOnboardingTourStep();
  const desktopPreviewContainerRef = useRef<HTMLDivElement>(null);
  const desktopPreviewContainerSize = useResizeObserver(desktopPreviewContainerRef);
  const isBusy = isPreparing || isSubmitting;
  const isOnboardingCustomizeStep = onboardingTourStep === 'store-customize';

  useEffect(() => {
    if (open) {
      setOverrideValues({});
    }
  }, [open, template?.id]);

  const overrideFields = useMemo(() => {
    if (!template) return [] as Array<{ fieldId: string; elementId: string; mediaType: string; source: any; label?: string }>;
    
    const allFields = collectDynamicFields(template).filter(f => (f.source as any)?.kind === 'override');
    
    // Group by overrideKey and mediaType
    const uniqueFieldsMap = new Map<string, typeof allFields[0]>();
    
    allFields.forEach(field => {
      const key = (field.source as any).overrideKey as string;
      const mediaType = field.mediaType;
      const uniqueKey = `${key}-${mediaType}`;
      
      if (!uniqueFieldsMap.has(uniqueKey)) {
        uniqueFieldsMap.set(uniqueKey, field);
      }
    });
    
    return Array.from(uniqueFieldsMap.values());
  }, [template]);

  const handleChangeForType = (key: string, mediaType: string) => (val: any) => {
    let normalized = val;
    if (mediaType === 'image') {
      if (val && typeof val === 'object') {
        const mediaId = (val as any).mediaId || null;
        const path = (val as any).path || '';
        const relativePath = (val as any).relativePath || null;
        
        normalized = { mediaId, path, relativePath };

      } else if (typeof val === 'string') {
        // Fallback for string input? assume path
        normalized = { mediaId: null, path: val, relativePath: null };
      } else {
        normalized = { mediaId: null, path: '', relativePath: null };
      }
    }
    setOverrideValues(prev => ({ ...prev, [key]: normalized }));
  };

  const canConfirm = overrideFields.length === 0 || overrideFields.every(f => {
    const key = (f.source as any).overrideKey as string;
    const val = overrideValues[key];
    if (f.mediaType === 'image') {
      if (val && typeof val === 'object') {
        const mid = (val as any).mediaId;
        const path = (val as any).path;
        const rel = (val as any).relativePath;
        
        // Allow valid mediaId OR valid path/relativePath
        const hasValidMediaId = typeof mid === 'string' && /^[0-9a-fA-F]{24}$/.test(mid);
        const hasValidPath = (typeof path === 'string' && path.length > 0) || (typeof rel === 'string' && rel.length > 0);
        
        return hasValidMediaId || hasValidPath;
      }
      return false;
    }
    return val !== undefined && val !== null && String(val).trim() !== '';
  });

  const handleConfirm = async () => {
    if (!template || isBusy) return;
    setIsPreparing(true);
    
    // Prepare overrides for export (convert to nested object)
    const overridesObj = Object.fromEntries(Object.entries(overrideValues).map(([k,v]) => [`overrides.${k}`, v]));
    
    let thumbnailData: string | undefined;
    try {
      thumbnailData = await generateThumbnail(template, overridesObj);
    } catch (err) {
      console.error('Failed to generate thumbnail for import:', err);
    }

    try {
      await onConfirm(overrideValues, thumbnailData);
    } finally {
      setIsPreparing(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isBusy) {
      return;
    }

    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-onboarding="store-template-import-dialog"
        className="sm:max-w-6xl p-0 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] border-0 shadow-2xl left-0 top-0 translate-x-0 translate-y-0 max-w-none max-h-none h-[100vh] w-screen flex flex-col overflow-hidden gap-0 rounded-none sm:left-[50%] sm:top-[50%] sm:h-[80vh] sm:min-h-[600px] sm:w-full sm:max-h-[90vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl sm:pt-0 sm:pb-0 bg-white"
        onEscapeKeyDown={(event) => {
          if (preventOutsideClose || isBusy) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (preventOutsideClose || isBusy) {
            event.preventDefault();
          }
        }}
      >
        <div className="px-5 sm:px-6 py-4 sm:py-5 shrink-0 z-20 bg-white border-b border-gray-100">
          <StandardDialogHeader
            icon={Download}
            title={template ? `Template importieren: ${template.title}` : "Template importieren"}
            description="Passe das Template an deinen Verein an, bevor du es importierst."
            className="p-0 border-none"
          />
        </div>

        <div className="flex flex-col lg:flex-row flex-1 overflow-y-auto lg:overflow-hidden bg-gray-50/50">
          {/* Left Column: Input Fields (Order 2 on Mobile, Order 1 on Desktop) */}
          <div
            className="order-2 lg:order-1 flex flex-col w-full lg:w-[420px] bg-white border-t lg:border-t-0 lg:border-r border-gray-100 h-auto lg:h-full lg:overflow-hidden shadow-sm lg:shadow-none z-10"
          >
             <div data-onboarding="store-template-import-customize" className="flex min-h-0 flex-1 flex-col">
               <div className="p-5 pb-2 flex-shrink-0">
                 <div className="flex items-center gap-2 mb-2">
                   <Label className="text-sm font-semibold text-gray-900">Inhalte anpassen</Label>
                   <Info className="h-3.5 w-3.5 text-gray-400" />
                 </div>
                 <p className="text-xs text-gray-500 leading-relaxed">
                   Fülle die Platzhalter aus, um das Template zu personalisieren. Die Vorschau aktualisiert sich automatisch.
                 </p>
               </div>
               
               {/* Replaced ScrollArea with standard div for better responsive behavior */}
               <div className="flex-1 px-5 pb-5 lg:overflow-y-auto">
                  <div className="space-y-5 pt-2">
                    {overrideFields.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                        <LayoutTemplate className="h-8 w-8 mb-2 opacity-50" />
                        <span className="text-sm">Keine Anpassungen erforderlich</span>
                      </div>
                    )}
                    
                    {overrideFields.map((f) => {
                      const key = (f.source as any).overrideKey as string;
                      const mediaType = f.mediaType as any;
                      return (
                        <div key={f.fieldId} className="space-y-2 group">
                          <div className="flex items-center gap-2">
                             <div className="flex h-6 w-6 items-center justify-center rounded-md bg-gray-100 text-gray-500">
                               {getMediaTypeIcon(mediaType, 'w-3.5 h-3.5')}
                             </div>
                             <Label className="text-xs font-medium text-gray-700 capitalize tracking-wider">
                               {key}
                             </Label>
                          </div>
                          
                          <div className="relative">
                            <SelectionStaticInputRenderer
                              value={overrideValues[key] ?? (mediaType === 'image' ? null : '')}
                              onChange={handleChangeForType(key, mediaType)}
                              mediaType={mediaType}
                              className="w-full"
                              selectionId={key}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
               </div>
             </div>
             
             <div className="px-5 sm:px-6 py-4 shrink-0 border-t border-gray-100 bg-white mt-auto sticky bottom-0 lg:static flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 flex">
                <div className="flex w-full sm:w-auto">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => onOpenChange(false)}
                    disabled={isBusy}
                    className="h-11 w-full justify-center rounded-xl px-4 text-sm font-semibold text-gray-600 transition-all hover:bg-gray-100 hover:text-gray-900 sm:w-auto sm:justify-start"
                  >
                    Abbrechen
                  </Button>
                </div>
                <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                  <PermissionFeatureWrapper
                    requiredPermission={Permission.MANAGE_ENTITIES}
                    requiredPlan={PlanKey.BASIC}
                  >
                    <Button
                      data-onboarding="store-template-import-confirm"
                      type="button"
                      disabled={isOnboardingCustomizeStep || !canConfirm || isBusy}
                      onClick={handleConfirm}
                      className="h-11 w-full px-6 sm:px-8 min-w-[140px] rounded-xl font-bold text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all sm:w-auto"
                    >
                      {isBusy ? (
                         <>
                           <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                           Importiere...
                         </>
                      ) : (
                         <>
                           <Download className="h-4 w-4 mr-2" />
                           Importieren
                         </>
                      )}
                    </Button>
                  </PermissionFeatureWrapper>
                </div>
             </div>
          </div>

          {/* Right Column: Preview (Order 1 on Mobile, Order 2 on Desktop) */}
          <div className="order-1 lg:order-2 flex-shrink-0 lg:flex-1 flex flex-col items-center justify-start p-6 lg:p-10 bg-gray-50/50 min-h-[220px] lg:h-full lg:min-h-0 lg:overflow-hidden lg:justify-center relative">
             <div className="absolute inset-0 pattern-grid-lg text-gray-100 opacity-50 pointer-events-none" />
             
             {template && (
               <>
                <div
                  className={cn(
                    "lg:hidden relative shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] rounded-2xl overflow-hidden ring-1 ring-black/5 bg-white z-10 mx-auto my-auto w-full",
                    template.canvas.height > template.canvas.width ? "max-w-[220px]" : "max-w-[340px]"
                  )}
                  style={{ aspectRatio: `${template.canvas.width} / ${template.canvas.height}` }}
                >
                  <SentrySectionBoundary
                    boundaryKey="store-template-mobile-preview"
                    title="Template-Vorschau konnte nicht gerendert werden"
                    description="Die mobile Store-Vorschau ist abgestürzt. Du kannst diesen Bereich neu laden."
                    className="flex h-full w-full items-center justify-center p-4"
                  >
                    <TemplateRenderer
                      template={template}
                      overrides={Object.fromEntries(Object.entries(overrideValues).map(([k, v]) => [`overrides.${k}`, v]))}
                      fitToContainer
                      showBorder={false}
                      shadow="none"
                      cornerRadius={0}
                    />
                  </SentrySectionBoundary>
                </div>

                <div ref={desktopPreviewContainerRef} className="hidden lg:flex flex-1 min-h-0 w-full items-center justify-center z-10">
                  <SentrySectionBoundary
                    boundaryKey="store-template-desktop-preview"
                    title="Template-Vorschau konnte nicht gerendert werden"
                    description="Die Desktop-Vorschau ist abgestürzt. Du kannst diesen Bereich neu laden."
                    className="flex h-full w-full items-center justify-center p-6"
                  >
                    <TemplateRenderer
                      template={template}
                      overrides={Object.fromEntries(Object.entries(overrideValues).map(([k, v]) => [`overrides.${k}`, v]))}
                      fitToContainer
                      resizeMode="contain"
                      containerSize={desktopPreviewContainerSize}
                      showBorder={false}
                      shadow="none"
                      cornerRadius={16}
                      className="shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] ring-1 ring-black/5"
                    />
                  </SentrySectionBoundary>
                </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
